import { useMemo } from 'react';

import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import { useBusinessDevelopmentStore } from 'stores/businessDevelopmentStore';

import {
  buildSlideoutCareTransitionsParams,
  buildSlideoutDischargesParams,
  buildSlideoutReferralsParams,
  buildSlideoutViewByOptions,
  entityFilterForViewBy,
  getDefaultSlideoutDimension,
  SlideoutViewByOption,
  TABLE_PIVOT,
  TablePivot,
  ViewBy,
} from '../constants';
import useBusinessDevelopmentAnalytics from '../query/useBusinessDevelopmentAnalytics';

export type ViewByCount = { label: string; count: number };

type QueryResult = { loading: boolean; rollups: AnalyticsResponseRollupCollection };

// Derive entity counts per View By option from the active pivot's query results.
// For single-dimension options, grouping:0 rows are filtered out of the rollup collection,
// so we count from responseData directly.
export function deriveViewByCounts(viewByOptions: SlideoutViewByOption[], pivotResults: QueryResult[]): ViewByCount[] {
  return viewByOptions.map((opt, i) => {
    const result = pivotResults[i];
    if (!result) return { label: opt.label, count: 0 };
    const count = opt.hasSubRows
      ? (result.rollups.groupings[1]?.dimensions.getCurrentDimensionValuesByIndex(0).length ?? 0)
      : result.rollups.analyticsResponse.responseData.filter(
          (row) => (row.grouping ?? 0) === 0 && (row.datesIndex ?? 0) === 0
        ).length;
    return { label: opt.label, count };
  });
}

export type SlideoutAnalyticsResult = {
  careTransitionsRollups: AnalyticsResponseRollupCollection;
  dischargesRollups: AnalyticsResponseRollupCollection;
  referralsRollups: AnalyticsResponseRollupCollection;
  breakdownRollups: AnalyticsResponseRollupCollection;
  viewByCounts: ViewByCount[];
  countsLoading: boolean;
  loading: boolean;
  activeOption: SlideoutViewByOption;
  viewByOptions: SlideoutViewByOption[];
};

// Run a query for each View By option against a given source builder.
// All 3 fire on slideout open; switching View By reads from cache.
// IMPORTANT: This function assumes exactly 3 View By options to keep hook count stable
// across renders. buildSlideoutViewByOptions must always return exactly 3 options.
function usePerOptionQueries(
  viewByOptions: SlideoutViewByOption[],
  builder: (option: SlideoutViewByOption) => ReturnType<typeof buildSlideoutCareTransitionsParams>,
  entityId: string,
  extraFilters: Record<string, unknown>,
  sourceLabel: string,
  enabled = true
): QueryResult[] {
  /* eslint-disable react-hooks/exhaustive-deps */
  const p0 = useMemo(() => builder(viewByOptions[0]), [viewByOptions[0]?.value]);
  const p1 = useMemo(() => (viewByOptions[1] ? builder(viewByOptions[1]) : p0), [viewByOptions[1]?.value]);
  const p2 = useMemo(() => (viewByOptions[2] ? builder(viewByOptions[2]) : p0), [viewByOptions[2]?.value]);

  const r0 = useBusinessDevelopmentAnalytics(p0, {
    queryKey: `bdSlideout:${sourceLabel}:${entityId}:${viewByOptions[0]?.value}`,
    extraFilters,
    enabled,
  });
  const r1 = useBusinessDevelopmentAnalytics(p1, {
    queryKey: `bdSlideout:${sourceLabel}:${entityId}:${viewByOptions[1]?.value}`,
    extraFilters,
    enabled: enabled && !!viewByOptions[1],
  });
  const r2 = useBusinessDevelopmentAnalytics(p2, {
    queryKey: `bdSlideout:${sourceLabel}:${entityId}:${viewByOptions[2]?.value}`,
    extraFilters,
    enabled: enabled && !!viewByOptions[2],
  });
  /* eslint-enable react-hooks/exhaustive-deps */

  return [r0, r1, r2];
}

export default function useSlideoutAnalytics(
  entityId: string,
  entityName: string,
  viewBy: ViewBy,
  tablePivot: TablePivot
): SlideoutAnalyticsResult {
  const slideoutDimension = useBusinessDevelopmentStore((s) => s.slideoutDimension);
  const groupTypeName = useBusinessDevelopmentStore((s) => s.selectedGroupType!.displayName);

  const viewByOptions = useMemo(() => buildSlideoutViewByOptions(viewBy, groupTypeName), [viewBy, groupTypeName]);
  const defaultDimension = getDefaultSlideoutDimension(viewBy);
  const activeOption =
    viewByOptions.find((o) => o.value === (slideoutDimension ?? defaultDimension)) ?? viewByOptions[0];
  const activeIndex = viewByOptions.indexOf(activeOption);

  const extraFilters = useMemo(
    () => entityFilterForViewBy(viewBy, entityId, entityName),
    [viewBy, entityId, entityName]
  );

  // Each source only fetches when its pivot is active.
  // All 3 View By options are pre-fetched per source so switching View By reads from cache.
  const ctResults = usePerOptionQueries(
    viewByOptions,
    buildSlideoutCareTransitionsParams,
    entityId,
    extraFilters,
    'careTransitions',
    tablePivot === TABLE_PIVOT.PATIENT_VOLUME
  );
  const dischResults = usePerOptionQueries(
    viewByOptions,
    buildSlideoutDischargesParams,
    entityId,
    extraFilters,
    'discharges',
    tablePivot === TABLE_PIVOT.DISCHARGES
  );
  const refResults = usePerOptionQueries(
    viewByOptions,
    buildSlideoutReferralsParams,
    entityId,
    extraFilters,
    'referrals',
    tablePivot === TABLE_PIVOT.REFERRALS
  );

  // Active option's results
  const careTransitions = ctResults[activeIndex] ?? ctResults[0];
  const discharges = dischResults[activeIndex] ?? dischResults[0];
  const referrals = refResults[activeIndex] ?? refResults[0];

  // Map pivot → results for lookups
  const pivotResultsMap: Record<TablePivot, QueryResult[]> = {
    [TABLE_PIVOT.PATIENT_VOLUME]: ctResults,
    [TABLE_PIVOT.DISCHARGES]: dischResults,
    [TABLE_PIVOT.REFERRALS]: refResults,
  };
  const pivotActiveMap: Record<TablePivot, QueryResult> = {
    [TABLE_PIVOT.PATIENT_VOLUME]: careTransitions,
    [TABLE_PIVOT.DISCHARGES]: discharges,
    [TABLE_PIVOT.REFERRALS]: referrals,
  };

  // Subtitle counts from the active pivot's queries
  const pivotResults = pivotResultsMap[tablePivot];
  const viewByCounts = deriveViewByCounts(viewByOptions, pivotResults);
  const countsLoading = pivotResults.some((c, i) => i < viewByOptions.length && c.loading);

  // Breakdown + tiles both use the active pivot's active View By result
  const activePivot = pivotActiveMap[tablePivot];

  return {
    careTransitionsRollups: careTransitions.rollups,
    dischargesRollups: discharges.rollups,
    referralsRollups: referrals.rollups,
    breakdownRollups: activePivot.rollups,
    viewByCounts,
    countsLoading,
    loading: activePivot.loading,
    activeOption,
    viewByOptions,
  };
}
